/**
 * UI-state persistence for the chatbot: rehydration, debounced saves, and cross-tab sync of the
 * composite payload (Shell isChatbotOpen/chatbotWidth + Conversation currentConversationId/mode).
 *
 * The payload spans two slices, so this is a genuinely cross-slice concern: it lives with the
 * effects coordinator (which reads both slices and passes their fields/setters in) rather than
 * inside either slice provider.
 */
import { useCallback, useEffect, useRef } from 'react';
import { CONVERSATION_MODES, type ConversationMode } from '../constants/modes';
import { CHATBOT_UI_STATE_KEY } from '../constants/storageKeys';
import { useStorageSync } from '../hooks/useStorageSync';

/**
 * Trailing-debounce window for persisting UI state to localStorage. Coalesces rapid changes (e.g.
 * a drag-resize that fires setChatbotWidth on every mousemove) into a single write. Exported so
 * tests can advance fake timers past the debounce instead of hardcoding the delay.
 */
export const UI_STATE_SAVE_DEBOUNCE_MS = 300;

/**
 * Shape of the UI state we persist to / rehydrate from localStorage. `mode` is the conversation
 * mode of the tab that WROTE the payload; payloads written before TANAI-2620 carry none.
 */
export type PersistedUIState = {
  isChatbotOpen?: boolean;
  chatbotWidth?: number;
  currentConversationId?: string | null;
  mode?: ConversationMode;
};

export const loadPersistedUIState = (): PersistedUIState => {
  try {
    const saved = localStorage.getItem(CHATBOT_UI_STATE_KEY);
    return saved ? (JSON.parse(saved) as PersistedUIState) : {};
  } catch (error) {
    console.warn('Failed to load chatbot UI state:', error);
    return {};
  }
};

type UIStateSnapshot = {
  isChatbotOpen: boolean;
  chatbotWidth: number;
  currentConversationId: string | null;
  mode: ConversationMode;
};

/**
 * Only a study tab authors the study-owned fields (isChatbotOpen, currentConversationId). Any other
 * mode keeps the stored ones and only contributes chatbotWidth, a preference shared by every mode:
 * otherwise opening a practice (or revision / teacher-tools) tab overwrote "chat closed, no
 * conversation" and the study tab lost both, live and after a reload (TANAI-2620). Merging instead
 * of skipping the write keeps rehydrating a non-study tab's own width. A study tab that has just
 * come back from another mode writes like a non-study one until the student uses its chat.
 */
function buildUIStateToPersist(snapshot: UIStateSnapshot, isRejoiningStudy: boolean): PersistedUIState {
  if (snapshot.mode === CONVERSATION_MODES.STUDY && !isRejoiningStudy) {
    return {
      isChatbotOpen: snapshot.isChatbotOpen,
      chatbotWidth: snapshot.chatbotWidth,
      currentConversationId: snapshot.currentConversationId,
      mode: snapshot.mode,
    };
  }
  const stored = loadPersistedUIState();
  return {
    isChatbotOpen: stored.isChatbotOpen ?? false,
    chatbotWidth: snapshot.chatbotWidth,
    currentConversationId: stored.currentConversationId ?? null,
    mode: snapshot.mode,
  };
}

type Params = UIStateSnapshot & {
  isAIEnabled: boolean;
  /**
   * A tab launched from a study chat card (TANAI-2622): it never writes the shared UI state and
   * never applies the one other tabs write, for its whole life. Fixed at mount.
   */
  isIsolatedTab?: boolean;
  setCurrentConversationId: (id: string | null) => void;
  openChatbot: () => void;
  closeChatbot: () => void;
  setChatbotWidth: (width: number) => void;
};

export function useAIChatUiStatePersistence({
  isAIEnabled,
  isIsolatedTab = false,
  isChatbotOpen,
  chatbotWidth,
  currentConversationId,
  mode,
  setCurrentConversationId,
  openChatbot,
  closeChatbot,
  setChatbotWidth,
}: Params): void {
  // The volatile fields the cross-tab handler compares against are kept in a ref synced by the
  // effect below, so the callback identity is `[]`-stable. useStorageSync re-subscribes the window
  // `storage` listener whenever this callback's identity changes, so a stable identity means the
  // listener is attached exactly once instead of churning on every UI state change.
  const externalUIStateRef = useRef({ mode, currentConversationId, isChatbotOpen, chatbotWidth });
  useEffect(() => {
    externalUIStateRef.current = { mode, currentConversationId, isChatbotOpen, chatbotWidth };
  }, [mode, currentConversationId, isChatbotOpen, chatbotWidth]);

  // Read through a ref so the cross-tab handler keeps its `[]`-stable identity.
  const isIsolatedTabRef = useRef(isIsolatedTab);

  // A tab that comes back to study from another mode (a practice that ends on /today or
  // /my-progress) still holds the closed chat and empty conversation it was left with when it
  // entered that mode. Publishing them would close the chat of the study tabs open in parallel, so
  // until the student opens the chat or a conversation is selected here, this tab keeps the stored
  // study fields like a non-study tab does. Effects run in order: the mode one goes first.
  const previousModeRef = useRef(mode);
  const isRejoiningStudyRef = useRef(false);
  useEffect(() => {
    if (previousModeRef.current === mode) return;
    isRejoiningStudyRef.current = mode === CONVERSATION_MODES.STUDY;
    previousModeRef.current = mode;
  }, [mode]);

  const lastStudyFieldsRef = useRef({ isChatbotOpen, currentConversationId });
  useEffect(() => {
    const last = lastStudyFieldsRef.current;
    const hasOpenedChat = isChatbotOpen && !last.isChatbotOpen;
    const hasSelectedConversation = currentConversationId !== null && currentConversationId !== last.currentConversationId;
    if (mode === CONVERSATION_MODES.STUDY && (hasOpenedChat || hasSelectedConversation)) {
      isRejoiningStudyRef.current = false;
    }
    lastStudyFieldsRef.current = { isChatbotOpen, currentConversationId };
  }, [isChatbotOpen, currentConversationId, mode]);

  const handleExternalUIStateChange = useCallback((newValue: PersistedUIState | null) => {
    if (isIsolatedTabRef.current) return;
    const current = externalUIStateRef.current;
    // Only a study writer's payload is applied. A legacy payload without `mode` is ignored too: it
    // can only come from a tab still running pre-TANAI-2620 code, possibly a practice tab, and two
    // study tabs resync on their next write or reload anyway.
    if (newValue && newValue.mode === CONVERSATION_MODES.STUDY && current.mode === CONVERSATION_MODES.STUDY) {
      if (newValue.currentConversationId !== current.currentConversationId) {
        setCurrentConversationId(newValue.currentConversationId ?? null);
      }
      if (newValue.isChatbotOpen !== current.isChatbotOpen) {
        if (newValue.isChatbotOpen) {
          openChatbot();
        } else {
          closeChatbot();
        }
      }
      if (newValue.chatbotWidth !== current.chatbotWidth) {
        setChatbotWidth(newValue.chatbotWidth as number);
      }
    }
    // Setters are permanently stable call-time-gated wrappers (useGatedActions), so an empty dep
    // array keeps the callback identity fixed while still dispatching correctly across
    // areActionsEnabled flips.
  }, []);

  const { setInternalChange: setUIInternalChange } = useStorageSync(CHATBOT_UI_STATE_KEY, handleExternalUIStateChange);

  /**
   * Save UI state to localStorage, marking the write as internal so the cross-tab listener does
   * not feed it back. See buildUIStateToPersist for what each mode may write.
   */
  const saveUIStateToStorage = useCallback(
    (stateToSave: UIStateSnapshot) => {
      try {
        setUIInternalChange(true);
        const uiState = buildUIStateToPersist(stateToSave, isRejoiningStudyRef.current);
        localStorage.setItem(CHATBOT_UI_STATE_KEY, JSON.stringify(uiState));
        // Reset flag after a short delay
        setTimeout(() => setUIInternalChange(false), 100);
      } catch (error) {
        console.warn('Failed to save chatbot UI state:', error);
      }
    },
    [setUIInternalChange]
  );

  // Save UI state when it changes, TRAILING-DEBOUNCED so a burst of changes (drag-resize)
  // collapses to a single write. The latest payload lives in a ref so both the debounce timer and
  // the flush paths read the freshest values. isAIEnabled is also mirrored into a ref so the
  // []-deps flush re-checks the gate rather than closing over a stale value.
  const isAIEnabledRef = useRef(isAIEnabled);
  useEffect(() => {
    isAIEnabledRef.current = isAIEnabled;
  }, [isAIEnabled]);

  const pendingUIStateRef = useRef<UIStateSnapshot | null>(null);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Gate: Skip (and drop any pending write) when AI features are disabled or the tab is isolated.
    if (!isAIEnabled || isIsolatedTab) {
      pendingUIStateRef.current = null;
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
      return undefined;
    }

    pendingUIStateRef.current = { isChatbotOpen, chatbotWidth, currentConversationId, mode };

    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveTimerRef.current = null;
      if (pendingUIStateRef.current) {
        saveUIStateToStorage(pendingUIStateRef.current);
        pendingUIStateRef.current = null;
      }
    }, UI_STATE_SAVE_DEBOUNCE_MS);

    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
    };
  }, [isAIEnabled, isIsolatedTab, isChatbotOpen, chatbotWidth, currentConversationId, mode, saveUIStateToStorage]);

  // Flush the pending debounced write immediately. Re-checks the gate via the ref (not a stale
  // captured isAIEnabled) and reads the freshest pending payload, so it is safe to call from any
  // teardown path. Identity-stable: saveUIStateToStorage only depends on the `[]`-stable
  // setInternalChange.
  const flushPendingUIState = useCallback(() => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    if (isAIEnabledRef.current && !isIsolatedTabRef.current && pendingUIStateRef.current) {
      saveUIStateToStorage(pendingUIStateRef.current);
      pendingUIStateRef.current = null;
    }
  }, [saveUIStateToStorage]);

  // React unmount cleanup does NOT run on a hard page teardown (reload / tab close), so pagehide
  // is the only reliable hook to persist a change still sitting inside the debounce window.
  useEffect(() => {
    window.addEventListener('pagehide', flushPendingUIState);
    return () => window.removeEventListener('pagehide', flushPendingUIState);
  }, [flushPendingUIState]);

  // Flush on React unmount so the last state is not lost when the provider unmounts in-page.
  useEffect(() => () => flushPendingUIState(), [flushPendingUIState]);
}
